import React from "react";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import ButtonGroup, {ButtonOption} from "@amzn/meridian/button-group";
import Icon from "@amzn/meridian/icon";
import plusTokens from "@amzn/meridian-tokens/base/icon/plus";
import printerTokens from "@amzn/meridian-tokens/base/icon/print";
import viewTokens from "@amzn/meridian-tokens/base/icon/view";
import {colorGreen400, colorGray100} from "@amzn/meridian-tokens/base/color";
import {FormattedDate, FormattedMessage} from "react-intl";
import {buildTitle, formatWeightUnit} from "../../util/CommonUtils";
import {WTN_MSG} from "../../commons/constants/WtnMessageIds";

const CONTAINER_STATUS = {
    OPEN: "OPEN",
    CLOSED: "CLOSED"
};

/**
 * Renders a single container card matching the WTN Containers design:
 * title / id / creation date / actions / status toggle.
 *
 * Action wiring:
 *   onPrintLabel(container)     — opens print window for container label
 *   onAddItems(container)       — opens the Add-Item modal
 *   onViewDetails(container)    — opens the container details modal
 *   onRequestClose(container)   — opens a confirmation before calling API
 */
const ContainerCard = ({
    container,
    onPrintLabel,
    onAddItems,
    onViewDetails,
    onRequestClose
}) => {
    const status = (container.status || "").toUpperCase();
    const isOpen = status === CONTAINER_STATUS.OPEN;

    const handleStatusChange = (nextStatus) => {
        if (nextStatus === CONTAINER_STATUS.CLOSED && isOpen) {
            onRequestClose && onRequestClose(container);
        }
        // CLOSED → OPEN transitions are intentionally unsupported in this CR
        // (no reopenEWasteContainer API exists). Ignore the click.
    };

    return (
        <Box
            type="outline"
            spacingInset="medium"
            className="wtn-container-card"
        >
            <Row alignmentHorizontal="justify" alignmentVertical="top" wrap="down">
                <Column spacing="small" width="80%">
                    <Text type="h200">{buildTitle(container)}</Text>
                    <Text type="b200" color="secondary">
                        <FormattedMessage
                            id={WTN_MSG.CONTAINER.ID}
                            defaultMessage="ID: {id}"
                            values={{id: container.containerDisplayId || ""}}
                        />
                    </Text>
                </Column>
                <ButtonGroup
                    value={isOpen ? CONTAINER_STATUS.OPEN : CONTAINER_STATUS.CLOSED}
                    onChange={handleStatusChange}
                >
                    <ButtonOption value={CONTAINER_STATUS.OPEN} disabled={!isOpen}>
                        <FormattedMessage id={WTN_MSG.CONTAINER.STATUS.OPEN} defaultMessage="OPEN" />
                    </ButtonOption>
                    <ButtonOption value={CONTAINER_STATUS.CLOSED} disabled={!isOpen}>
                        <FormattedMessage id={WTN_MSG.CONTAINER.STATUS.CLOSE} defaultMessage="CLOSE" />
                    </ButtonOption>
                </ButtonGroup>
            </Row>

            {/* Capacity bar */}
            {container.maxWeight && container.usage && container.usage.weight && container.maxWeight.weight > 0 && (
                <Row spacingInset="small none none none" alignmentHorizontal="justify" alignmentVertical="center">
                    <Column spacing="none" width="100%">
                        <Row alignmentHorizontal="justify">
                            <Text type="b200" tag="strong">
                                {container.usage.weight.weight}
                            </Text>
                            <Text type="b200" color="secondary">
                                {container.maxWeight.weight} {formatWeightUnit(container.maxWeight.unit)}
                            </Text>
                        </Row>
                        <div style={{
                            width: "100%",
                            height: "8px",
                            backgroundColor: colorGray100,
                            borderRadius: "4px",
                            marginTop: "4px"
                        }}>
                            <div style={{
                                width: `${Math.min(100, (container.usage.weight.weight / container.maxWeight.weight) * 100)}%`,
                                height: "100%",
                                backgroundColor: colorGreen400,
                                borderRadius: "4px"
                            }} />
                        </div>
                    </Column>
                </Row>
            )}

            {/* Creation date */}
            <Row spacingInset="small none none none">
                <Text type="b200" color="secondary">
                    <FormattedMessage
                        id={WTN_MSG.CONTAINER.CREATED_AT}
                        defaultMessage="Container Creation Date: {date}"
                        values={{
                            date: container.createdAt
                                ? <FormattedDate value={container.createdAt} year="numeric" month="2-digit" day="2-digit" />
                                : "—"
                        }}
                    />
                </Text>
            </Row>

            {/* Action buttons */}
            <Row spacing="small" spacingInset="medium none none none">
                <Button
                    size="small"
                    type="primary"
                    onClick={() => onPrintLabel && onPrintLabel(container)}
                >
                    <Icon tokens={printerTokens} />
                    <FormattedMessage id={WTN_MSG.CONTAINER.PRINT_LABELS} defaultMessage="Print Labels" />
                </Button>
                <Button
                    size="small"
                    type="primary"
                    onClick={() => onAddItems && onAddItems(container)}
                >
                    <Icon tokens={plusTokens} />
                    <FormattedMessage id={WTN_MSG.CONTAINER.ADD_ITEMS} defaultMessage="Add Items" />
                </Button>
                <Button
                    size="small"
                    type="primary"
                    onClick={() => onViewDetails && onViewDetails(container)}
                >
                    <Icon tokens={viewTokens} />
                    <FormattedMessage id={WTN_MSG.CONTAINER.VIEW_DETAILS} defaultMessage="View container items and details" />
                </Button>
            </Row>
        </Box>
    );
};

export default ContainerCard;



// WEBPACK FOOTER //
// ./src/components/wtn/ContainerCard.js